import Group from 'components/utils/Group'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { EmptyArray, groupBy, min, sortByAll, sum } from 'lib/array/arrayUtils'
import React, { useMemo } from 'react'
import { getAvailablePackagesByRoom, PackagesOptionsViewsByRoom } from 'selectors/offerPage/multipleRoomBookingSelectors'
import Pane from 'components/Common/Pane'
import MobileMultipleRoomsSameRoomOption from './MobileMultipleRoomsSameRoomOption'
import { MultipleRoomPackage } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'

interface Props {
  rooms: Array<App.RoomOccupants>;
  availablePackagesByRoom: PackagesOptionsViewsByRoom;
  multipleRoomPackages: Array<MultipleRoomPackage>;
  headerRef: React.RefObject<HTMLDivElement | null>;
  showSelection: boolean;
}

function sortByAvailability(options: Array<OfferPackageOptionView>, roomsLength: number) {
  return options[0].availableRooms ?? 0 - roomsLength
}

function sortByPrice(options: Array<OfferPackageOptionView>) {
  return min(options, option => option.hotelTotal)
}

function sortBySelectedAvailability(options: Array<OfferPackageOptionView>, showSelection: boolean, multipleRoomPackages: Array<MultipleRoomPackage>, roomsLength: number) {
  if (!showSelection) return 0
  const availableRoomsTotal = sum(options, option => option.availableRooms ?? 0)
  if (availableRoomsTotal < roomsLength) return 0
  // Same for both rooms
  const optionRoomType = options[0].package.roomType?.id
  const isSelected = multipleRoomPackages.some(selectedRoom => selectedRoom.roomTypeId === optionRoomType)
  return isSelected ? 1 : 0
}

function MobileMultipleRoomsSameRoomContent(props: Props) {
  const { rooms, availablePackagesByRoom, multipleRoomPackages, showSelection, headerRef } = props
  const availablePackages = availablePackagesByRoom[rooms[0].roomId]

  const optionsByRoomType = useMemo(() => {
    const rawOptions = Array.from(groupBy(availablePackages, (pkg) => pkg?.package.roomType?.id).values())
    return sortByAll(rawOptions, [
      // First order by availability of rooms in the room type from having the most to the least
      { selector: pkg => sortByAvailability(pkg, rooms.length), direction: 'desc' },
      // Then order by price
      { selector: sortByPrice, direction: 'asc' },
      // First order by room that has a package selection (if selection is to be shown)
      { selector: pkg => sortBySelectedAvailability(pkg, showSelection, multipleRoomPackages, rooms.length), direction: 'desc' },
    ])
  },
  [availablePackages, multipleRoomPackages, rooms.length, showSelection])

  return (
    <Pane background="neutral-six">
      <Group direction="vertical" gap={8}>
        {optionsByRoomType.map((roomOptions) => <MobileMultipleRoomsSameRoomOption
          key={roomOptions[0].package.roomType?.id}
          packageOptions={roomOptions}
          showSelection={showSelection}
          headerRef={headerRef}
        />)}
      </Group>
    </Pane>
  )
}

type MappedProps = Pick<Props,
  'rooms'
  | 'availablePackagesByRoom'
  | 'multipleRoomPackages'
>

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  rooms: state.rooms ?? EmptyArray as Array<App.RoomOccupants>,
  availablePackagesByRoom: getAvailablePackagesByRoom(state, appState),
  multipleRoomPackages: state.multipleRoomPackages,
}), MobileMultipleRoomsSameRoomContent)
